import {
  collection,
  doc,
  getDocs,
  deleteDoc,
  query,
  where,
  orderBy,
  writeBatch,
  setDoc,
  serverTimestamp,
  increment,
  Unsubscribe,
  QueryDocumentSnapshot,
  DocumentData,
} from '@firebase/firestore';
import { db, app } from './firebase';
import { onSnapshotWithRetry } from './snapshotRetry';
import { COLLECTIONS } from './collections';
import { AppNotification } from '../types';
import { paginatedQuery } from '@utils/firestore';
import { NotificationSchema } from '../schemas/firestoreSchemas';

const PAGE_SIZE = 20;

async function hashToken(token: string): Promise<string> {
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const encoded = new TextEncoder().encode(token);
    const hash = await crypto.subtle.digest('SHA-256', encoded);
    return Array.from(new Uint8Array(hash))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, 20);
  }
  return token.slice(0, 20);
}

export const notificationsApi = {
  async getNotifications(
    userId: string,
    lastDoc?: QueryDocumentSnapshot<DocumentData>
  ): Promise<{
    notifications: AppNotification[];
    lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  }> {
    const result = await paginatedQuery(
      collection(db, COLLECTIONS.NOTIFICATIONS),
      [where('recipientId', '==', userId), orderBy('createdAt', 'desc')],
      lastDoc,
      PAGE_SIZE,
      NotificationSchema
    );
    return { notifications: result.items, lastDoc: result.lastDoc };
  },

  // userId is needed to keep the denormalized unreadCount badge counter on
  // the user doc in step (sendNotification increments it server-side).
  async markAsRead(notificationId: string, userId: string): Promise<void> {
    const batch = writeBatch(db);
    batch.update(doc(db, COLLECTIONS.NOTIFICATIONS, notificationId), { read: true });
    batch.update(doc(db, COLLECTIONS.USERS, userId), { unreadCount: increment(-1) });
    await batch.commit();
  },

  async markAllAsRead(userId: string): Promise<void> {
    const q = query(
      collection(db, COLLECTIONS.NOTIFICATIONS),
      where('recipientId', '==', userId),
      where('read', '==', false)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return;

    // Firestore batch limit is 500 writes; reserve one slot for the
    // user-doc counter update.
    const BATCH_LIMIT = 499;
    const docs = snapshot.docs.slice(0, BATCH_LIMIT);
    const batch = writeBatch(db);
    for (const d of docs) {
      batch.update(d.ref, { read: true });
    }
    // All unread handled → zero the counter (also self-heals drift). With
    // 500+ unread, decrement by what this batch actually covered.
    if (snapshot.size <= BATCH_LIMIT) {
      batch.update(doc(db, COLLECTIONS.USERS, userId), { unreadCount: 0 });
    } else {
      batch.update(doc(db, COLLECTIONS.USERS, userId), { unreadCount: increment(-docs.length) });
    }
    await batch.commit();
  },

  // Badge counter: a single-doc listener on the user doc (1 read per
  // change) instead of streaming the full unread set, which re-billed
  // every unread doc on each session's initial snapshot.
  subscribeToUnreadCount(userId: string, callback: (count: number) => void): Unsubscribe {
    return onSnapshotWithRetry(doc(db, COLLECTIONS.USERS, userId), (snapshot) => {
      const raw = snapshot.data()?.unreadCount;
      callback(typeof raw === 'number' && raw > 0 ? raw : 0);
    });
  },

  async registerDevice(
    userId: string,
    token: string,
    platform: 'web' | 'ios' | 'android'
  ): Promise<void> {
    const deviceId = await hashToken(token);
    const deviceRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.DEVICES, deviceId);
    await setDoc(
      deviceRef,
      {
        token,
        platform,
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );
  },

  async removeDevice(userId: string, token: string): Promise<void> {
    const deviceId = await hashToken(token);
    const deviceRef = doc(db, COLLECTIONS.USERS, userId, COLLECTIONS.DEVICES, deviceId);
    await deleteDoc(deviceRef);
  },

  /**
   * Request browser notification permission (web-only), then lazily initialize
   * FCM and register the device token. The messaging import is kept dynamic so
   * it is never evaluated on native bundles.
   *
   * Returns the new permission state so the caller can update UI accordingly.
   * Throws on FCM errors so the caller can surface a warning.
   */
  async registerWebPushToken(userId: string): Promise<NotificationPermission> {
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      // Dynamic import keeps this code path out of native bundles entirely.
      const { getMessaging, getToken } = await import('@firebase/messaging');
      const messaging = getMessaging(app);
      const registration = await navigator.serviceWorker.ready;
      const token = await getToken(messaging, {
        vapidKey: process.env.EXPO_PUBLIC_FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration,
      });
      if (token) {
        await notificationsApi.registerDevice(userId, token, 'web');
      }
    }
    return permission;
  },
};
